import React, { useCallback } from 'react';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import type { IComponentServiceProps } from '@/pages/dashboard/components/PageContainer/type';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';
import { PR_SERVICE_PAYLOAD_PRESET_MAP } from '@home/pages/studio/pr/prStudioPayloadPresets';

/** Studio PR：`pageConfig` 中 `prPayloadPreset`（见 `prStudioPayloadPresets.ts` → `_utils`） */
export interface IPrStudioServiceProps extends IComponentServiceProps {
  prPayloadPreset?: string;
}

/**
 * Studio PR 专用接口层：把 `prPayloadPreset` 转成 `Service` 的 `beforeRequest`，
 * 不修改通用 `Service.tsx`。
 */
const PrStudioService: React.FC<IPrStudioServiceProps> = (props) => {
  const { prPayloadPreset, beforeRequest, ...rest } = props;

  const bridgedBeforeRequest = useCallback(
    (payload: Record<string, unknown>) => {
      if (prPayloadPreset && typeof prPayloadPreset === 'string') {
        const presetFn = PR_SERVICE_PAYLOAD_PRESET_MAP[prPayloadPreset];
        if (presetFn) {
          return presetFn(payload as Record<string, unknown>);
        }
      }
      if (beforeRequest) {
        if (typeof beforeRequest === 'string') {
          return stringToFunction(beforeRequest)(payload);
        }
        if (typeof beforeRequest === 'function') {
          return beforeRequest(payload);
        }
      }
      return payload;
    },
    [beforeRequest, prPayloadPreset],
  );

  const presetHit =
    !!prPayloadPreset &&
    typeof prPayloadPreset === 'string' &&
    !!PR_SERVICE_PAYLOAD_PRESET_MAP[prPayloadPreset];

  const useBridge = presetHit || !!beforeRequest;

  return (
    <Service
      {...(rest as IComponentServiceProps)}
      beforeRequest={useBridge ? bridgedBeforeRequest : undefined}
    />
  );
};

export default PrStudioService;
